import {first} from 'lodash';

import {ExtendedError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {CancelToken} from '../../../helpers/asyncCancelHelpers';
import {buildPatchedChannelsForInbox} from '../../../helpers/models/channelHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {ApiInboxDataGroup, DataGroupModel} from '../../../models/api/dataGroupModel';
import {ApiInbox, ApiInboxes, InboxModel} from '../../../models/api/inboxModel';
import {TagModel} from '../../../models/api/tagModel';
import {TeammateInboxesUpdate} from '../../../models/api/teammateInboxesUpdateModel';
import {deleteAsync, fetchApiAsync, putApiAsync} from '../../../services/http/http';
import {HttpError, HttpNetworkError, HttpTimeoutError} from '../../../services/http/httpError';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {getFlatChannels} from '../channels/channelsSelectors';
import {getCompanyUrl} from '../company/companySelectors';
import {updateDataAction} from '../dataGroupsActions';
import {getFlatInboxes, getInboxes} from './inboxesSelectors';

/*
 * Action types.
 */

export enum InboxesActionsEnum {
  TEAMMATE_INBOXES_UPDATED = '@@data/inboxes/TEAMMATE_INBOXES_UPDATED',
  INBOX_DELETED = '@@data/inboxes/INBOX_DELETED',
}

/*
 * Types.
 */

export interface TeammateInboxesUpdatedAction extends ActionWithPayload<TeammateInboxesUpdate> {
  type: InboxesActionsEnum.TEAMMATE_INBOXES_UPDATED;
}

export interface InboxDeletedAction extends ActionWithPayload<number> {
  type: InboxesActionsEnum.INBOX_DELETED;
}

/*
 * Action creators.
 */

export function updateTeammateInboxesAction(update: TeammateInboxesUpdate) {
  return {
    type: InboxesActionsEnum.TEAMMATE_INBOXES_UPDATED,
    payload: update,
  };
}

export function pushInboxDeleted(inboxId: number): InboxDeletedAction {
  const action: InboxDeletedAction = {
    type: InboxesActionsEnum.INBOX_DELETED,
    payload: inboxId,
  };
  return action;
}

export function deleteInboxAction(inboxId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const inboxes = getInboxes(getState());
    const inbox = inboxes[inboxId];

    if (!inbox) {
      throw new Error(`No inbox found for id ${inboxId}`);
    }

    await deleteAsync(inbox.url);
    dispatch(pushInboxDeleted(inboxId));
  };
}

export function updateInboxAction(newInbox: InboxModel): ThunkAction {
  return (dispatch, getState) => {
    const flatChannels = getFlatChannels(getState());

    const channels = buildPatchedChannelsForInbox(flatChannels, newInbox);
    const result = {inboxes: [newInbox], channels};
    dispatch(updateDataAction(result));
  };
}

export function updateInboxAsyncAction(inbox: InboxModel): ThunkAsyncAction {
  return async (dispatch) => {
    try {
      const newInbox = await putApiAsync(ApiInbox, inbox.url, ApiInbox.serialize(inbox));
      dispatch(updateInboxAction(newInbox));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchInboxByIdAsyncAction(id: number): ThunkAsyncAction<InboxModel | undefined> {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);

    const inboxes = getInboxes(getState());
    const inbox = inboxes[id];
    if (inbox) {
      return inbox;
    }

    try {
      const result = await fetchApiAsync(ApiInboxDataGroup, `${companyUrl}/inboxes/${id}`);
      dispatch(updateDataAction(result));

      return findInboxInDataGroup(result);
    } catch (error) {
      if (!(error instanceof HttpNetworkError || error instanceof HttpTimeoutError)) {
        reportToBugsnag(new ExtendedError('Failed to fetch inbox by id', error));
      }

      return undefined;
    }
  };
}

async function maybeFetchMissingInboxesAsync(
  missingInboxIds: ReadonlyArray<number>,
  companyUrl: string,
  cancelToken?: CancelToken,
): Promise<ReadonlyArray<InboxModel> | undefined> {
  if (missingInboxIds.length === 0) {
    return undefined;
  }

  try {
    const inboxesUrl = `${companyUrl}/inboxes/?ids=${missingInboxIds.join(',')}`;
    return await fetchApiAsync(ApiInboxes, inboxesUrl, {cancelToken});
  } catch (err) {
    if (!(err instanceof HttpNetworkError || err instanceof HttpTimeoutError)) {
      reportToBugsnag(new ExtendedError('Failed to fetch inboxes by ids', err));
    }
    return undefined;
  }
}

export function fetchTagInboxesAsyncAction(tag: TagModel, cancelToken: CancelToken): ThunkAsyncAction<void> {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);
    const storeInboxes = getInboxes(state);

    const missingInboxIds = tag.inboxIds.filter((id) => !storeInboxes[id]);

    const fetchedInboxes = await maybeFetchMissingInboxesAsync(missingInboxIds, companyUrl, cancelToken);
    if (fetchedInboxes) {
      dispatch(updateDataAction({inboxes: [...fetchedInboxes]}));
    }
  };
}

export function fetchInboxByAliasAsyncAction(alias: string): ThunkAsyncAction<InboxModel | undefined> {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);

    const flatInboxes = getFlatInboxes(state);
    const inbox = alias ? flatInboxes.find((i) => i?.alias === alias) : undefined;
    if (inbox) {
      return inbox;
    }

    try {
      const result = await fetchApiAsync(
        ApiInboxDataGroup,
        `${companyUrl}/inboxes/${encodeURIComponent(alias)}`,
      );
      dispatch(updateDataAction(result));

      return findInboxInDataGroup(result);
    } catch (error) {
      if (!(error instanceof HttpNetworkError || error instanceof HttpTimeoutError)) {
        reportToBugsnag(new ExtendedError('Failed to fetch inbox by alias', error));
      }

      return undefined;
    }
  };
}

/*
 * Helpers.
 */

function findInboxInDataGroup(dataGroup: DataGroupModel) {
  return first(dataGroup.inboxes || []);
}

/*
 * Union type.
 */

export type InboxesAction = TeammateInboxesUpdatedAction | InboxDeletedAction;
